import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

import type { ManualDetail } from "@/api/db/manuals";
import type { DocumentBlock } from "@/api/document/block";
import { documentStyles } from "@/api/document/styles";
import { ManualRules } from "@/api/manual/rules";
import { DocumentEditor, useDocumentEditor } from "@/components/document-editor/editor";
import { setLintFlags } from "@/components/document-editor/lint";
import { DocumentToolbar } from "@/components/document-editor/toolbar";
import { useManualDetail } from "@/hooks/use-manuals";
import { errorCode } from "@/lib/error-code";
import { ManualLabels } from "@/lib/labels/manuals";

import { DeliverAction } from "./document-actions/deliver-action";
import { RevokeAction } from "./document-actions/revoke-action";
import { SaveAction } from "./document-actions/save-action";
import { DocumentChrome } from "./document-chrome";
import { DocumentDetails } from "./document-details";
import { DocumentSkeleton } from "./document-skeleton";
import { DocumentCover, DocumentFooter } from "./paper-frame";

export function ManualDocument({ id }: { id: number }) {
	const detail = useManualDetail(id);

	if (detail.isError) {
		return <DocumentError message={ManualLabels.error(errorCode(detail.error))} />;
	}

	if (detail.data === undefined) {
		return (
			<div className="bg-background">
				<DocumentSkeleton />
			</div>
		);
	}

	return <ManualEditor key={detail.data.id} manual={detail.data} />;
}

function DocumentError({ message }: { message: string }) {
	return (
		<div className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 bg-background px-4 text-center">
			<p className="text-base text-foreground/70">{message}</p>

			<Link
				to="/clinica"
				className="inline-flex items-center gap-2 rounded-md border border-foreground/15 px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
			>
				<ArrowLeft className="size-4" />
				Voltar
			</Link>
		</div>
	);
}

function ManualEditor({ manual }: { manual: ManualDetail }) {
	const [dirty, setDirty] = useState(false);
	const [warnings, setWarnings] = useState(manual.warnings);
	const saved = useRef(manual.blocks);

	const editor = useDocumentEditor({
		blocks: manual.blocks,
		fields: manual.fields,
		onUpdate: (blocks) => {
			setDirty(JSON.stringify(blocks) !== JSON.stringify(saved.current));
			setWarnings(ManualRules.lintBlocks(blocks));
		},
	});

	useEffect(() => {
		setLintFlags(editor, warnings);
	}, [editor, warnings]);

	const markSaved = (blocks: DocumentBlock[]) => {
		saved.current = blocks;
		setDirty(false);
	};

	return (
		<div className="bg-background">
			<DocumentChrome
				manual={manual}
				dirty={dirty}
				toolbar={<DocumentToolbar editor={editor} fields={manual.fields} kind="manual" />}
			>
				<SaveAction manualId={manual.id} editor={editor} dirty={dirty} onSaved={markSaved} />
				<DeliverAction manual={manual} dirty={dirty} />
				<RevokeAction manual={manual} />
			</DocumentChrome>

			<DocumentEditor
				editor={editor}
				fields={manual.fields}
				kind="manual"
				stylesheet={documentStyles.manual}
				header={<DocumentCover patient={manual.patient} />}
				footer={<DocumentFooter />}
			/>

			<div className="mx-auto max-w-170 px-4 py-6">
				<DocumentDetails manual={manual} warnings={warnings} />
			</div>
		</div>
	);
}
